// C:\IntegraNube_WebStore\src\pages\RetailLanding.js
/* ------------------------------------------------------------------
 * IntegraNube WebStore Retail - landing comercial
 *
 * Se muestra exclusivamente cuando el proyecto se visita mediante:
 *  - retail.integranube.cl
 *  - webstore-retail.pages.dev
 *  - previews *.webstore-retail.pages.dev
 *
 * Esta página NO depende de /tienda ni de ningún tenant.
 * ------------------------------------------------------------------ */

import React, { useEffect } from 'react';

import {
  Box,
  Button,
  Chip,
  Container,
  CssBaseline,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import {
  ArrowForwardRounded,
  AutoAwesomeRounded,
  CheckCircleRounded,
  CloudDoneRounded,
  DevicesRounded,
  Inventory2Rounded,
  LanguageRounded,
  ShoppingCartCheckoutRounded,
  StorefrontRounded,
  SyncRounded,
} from '@mui/icons-material';

import {
  createTheme,
  ThemeProvider,
} from '@mui/material/styles';

const INTEGRANUBE_URL = 'https://integranube.cl';

const landingTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1769e0',
    },
    secondary: {
      main: '#0f172a',
    },
    background: {
      default: '#f7f9fc',
      paper: '#ffffff',
    },
    text: {
      primary: '#0f172a',
      secondary: '#5b6474',
    },
  },
  shape: {
    borderRadius: 16,
  },
});

const benefits = [
  {
    icon: <SyncRounded />,
    title: 'Información sincronizada',
    description:
      'Productos, precios y configuración comercial conectados con IntegraNube.',
  },
  {
    icon: <Inventory2Rounded />,
    title: 'Catálogo siempre disponible',
    description:
      'Una vitrina digital preparada para mostrar el catálogo de forma clara y ordenada.',
  },
  {
    icon: <ShoppingCartCheckoutRounded />,
    title: 'Experiencia de compra',
    description:
      'Carrito, proceso de compra y seguimiento dentro de una experiencia orientada a retail.',
  },
  {
    icon: <LanguageRounded />,
    title: 'Dominio propio',
    description:
      'La tienda puede operar bajo el dominio comercial de cada empresa.',
  },
  {
    icon: <DevicesRounded />,
    title: 'Diseño adaptable',
    description:
      'Una experiencia preparada para computadores, tablets y dispositivos móviles.',
  },
  {
    icon: <CloudDoneRounded />,
    title: 'Infraestructura administrada',
    description:
      'Publicación web sobre infraestructura moderna, sin mantener una plataforma separada.',
  },
];

function RetailLanding() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'IntegraNube WebStore Retail | Su tienda online integrada';

    let meta = document.querySelector('meta[name="description"]');
    const previousDescription = meta?.getAttribute('content') || null;
    let createdMeta = false;

    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
      createdMeta = true;
    }

    meta.setAttribute(
      'content',
      'IntegraNube WebStore Retail permite llevar el catálogo de una empresa a una tienda online moderna, integrada y con dominio propio.'
    );

    return () => {
      document.title = previousTitle;

      if (createdMeta) {
        meta?.remove();
      } else if (meta && previousDescription !== null) {
        meta.setAttribute('content', previousDescription);
      }
    };
  }, []);

  return (
    <ThemeProvider theme={landingTheme}>
      <CssBaseline />

      <Box sx={styles.page}>
        <Box component="header" sx={styles.header}>
          <Container maxWidth="lg">
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
              sx={{ minHeight: 72 }}
            >
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Box sx={styles.brandMark}>
                  <StorefrontRounded sx={{ fontSize: 22 }} />
                </Box>

                <Box>
                  <Typography sx={styles.brandName}>IntegraNube</Typography>
                  <Typography sx={styles.brandProduct}>WebStore Retail</Typography>
                </Box>
              </Stack>

              <Button
                href={INTEGRANUBE_URL}
                target="_blank"
                rel="noreferrer"
                color="inherit"
                endIcon={<ArrowForwardRounded />}
                sx={styles.headerButton}
              >
                Conocer IntegraNube
              </Button>
            </Stack>
          </Container>
        </Box>

        <Box component="main">
          <Box sx={styles.hero}>
            <Container maxWidth="lg">
              <Grid container spacing={{ xs: 5, md: 7 }} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Chip
                    icon={<AutoAwesomeRounded />}
                    label="IntegraNube WebStore"
                    sx={styles.eyebrow}
                  />

                  <Typography component="h1" sx={styles.heroTitle}>
                    Aquí podría estar su próxima{' '}
                    <Box component="span" sx={styles.heroAccent}>
                      tienda online
                    </Box>
                  </Typography>

                  <Typography sx={styles.heroText}>
                    Una experiencia de compra moderna, conectada con la operación
                    de su empresa y presentada bajo su propia marca y dominio.
                  </Typography>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    sx={{ mt: 3.5 }}
                  >
                    <Button
                      href={INTEGRANUBE_URL}
                      target="_blank"
                      rel="noreferrer"
                      variant="contained"
                      size="large"
                      endIcon={<ArrowForwardRounded />}
                      sx={styles.primaryButton}
                    >
                      Solicitar información
                    </Button>

                    <Button
                      href="#caracteristicas"
                      variant="outlined"
                      size="large"
                      sx={styles.secondaryButton}
                    >
                      Ver características
                    </Button>
                  </Stack>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1, sm: 2.5 }}
                    sx={{ mt: 3 }}
                  >
                    {[
                      'Dominio propio',
                      'Catálogo conectado',
                      'Diseño adaptable',
                    ].map((item) => (
                      <Stack
                        key={item}
                        direction="row"
                        alignItems="center"
                        spacing={0.7}
                      >
                        <CheckCircleRounded sx={styles.checkIcon} />
                        <Typography sx={styles.heroCheckText}>{item}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <StorePreview />
                </Grid>
              </Grid>
            </Container>
          </Box>

          <Box id="caracteristicas" sx={styles.featuresSection}>
            <Container maxWidth="lg">
              <Box sx={styles.sectionIntro}>
                <Typography sx={styles.sectionKicker}>
                  UNA TIENDA CONECTADA
                </Typography>
                <Typography component="h2" sx={styles.sectionTitle}>
                  Del catálogo a la venta online, en una misma operación
                </Typography>
                <Typography sx={styles.sectionText}>
                  WebStore Retail está pensado para extender la presencia digital
                  de una empresa sin transformar el ecommerce en una plataforma
                  aislada del resto de su gestión.
                </Typography>
              </Box>

              <Grid container spacing={2.5}>
                {benefits.map((benefit) => (
                  <Grid item xs={12} sm={6} md={4} key={benefit.title}>
                    <Paper elevation={0} sx={styles.featureCard}>
                      <Box sx={styles.featureIcon}>{benefit.icon}</Box>
                      <Typography sx={styles.featureTitle}>
                        {benefit.title}
                      </Typography>
                      <Typography sx={styles.featureText}>
                        {benefit.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>

          <Box sx={styles.domainSection}>
            <Container maxWidth="lg">
              <Paper elevation={0} sx={styles.domainCard}>
                <Grid container spacing={4} alignItems="center">
                  <Grid item xs={12} md={7}>
                    <Typography sx={styles.sectionKicker}>SU MARCA, SU DOMINIO</Typography>
                    <Typography component="h2" sx={styles.domainTitle}>
                      Una tienda que se presenta como parte natural de su empresa
                    </Typography>
                    <Typography sx={styles.domainText}>
                      La experiencia puede publicarse bajo el dominio comercial de
                      cada negocio, manteniendo la identidad visual y la información
                      configurada para la tienda.
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <Box sx={styles.domainBrowser}>
                      <Stack direction="row" spacing={0.7} sx={{ mb: 1.2 }}>
                        <Box sx={styles.browserDot} />
                        <Box sx={styles.browserDot} />
                        <Box sx={styles.browserDot} />
                      </Stack>
                      <Box sx={styles.domainAddress}>https://www.suempresa.cl</Box>
                      <Divider sx={{ my: 2 }} />
                      <Typography sx={styles.domainMiniLabel}>WebStore Retail</Typography>
                      <Typography sx={styles.domainMiniTitle}>
                        Su catálogo, disponible para comprar online.
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Container>
          </Box>

          <Box sx={styles.ctaSection}>
            <Container maxWidth="md">
              <Box sx={{ textAlign: 'center' }}>
                <Typography component="h2" sx={styles.ctaTitle}>
                  Su próxima vitrina digital puede comenzar aquí
                </Typography>
                <Typography sx={styles.ctaText}>
                  IntegraNube WebStore permite sumar una experiencia de ecommerce
                  conectada con la información comercial de su operación.
                </Typography>

                <Button
                  href={INTEGRANUBE_URL}
                  target="_blank"
                  rel="noreferrer"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardRounded />}
                  sx={styles.ctaButton}
                >
                  Conocer IntegraNube
                </Button>
              </Box>
            </Container>
          </Box>
        </Box>

        <Box component="footer" sx={styles.footer}>
          <Container maxWidth="lg">
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              spacing={1}
            >
              <Typography sx={styles.footerText}>
                IntegraNube WebStore Retail
              </Typography>
              <Typography sx={styles.footerText}>
                Una extensión digital de IntegraNube
              </Typography>
            </Stack>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

function StorePreview() {
  const products = [
    { name: 'Producto destacado', price: '$24.990' },
    { name: 'Nueva colección', price: '$18.490' },
    { name: 'Más vendido', price: '$32.990' },
  ];

  return (
    <Box sx={styles.previewWrap}>
      <Box sx={styles.glowOne} />
      <Box sx={styles.glowTwo} />

      <Paper elevation={0} sx={styles.browser}>
        <Stack direction="row" alignItems="center" spacing={0.7} sx={styles.browserTop}>
          <Box sx={styles.browserDot} />
          <Box sx={styles.browserDot} />
          <Box sx={styles.browserDot} />
          <Box sx={styles.browserUrl}>www.suempresa.cl</Box>
        </Stack>

        <Box sx={styles.previewStoreHeader}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={styles.previewLogo}>S</Box>
            <Box>
              <Typography sx={styles.previewBrand}>SU EMPRESA</Typography>
              <Typography sx={styles.previewBrandSub}>Tienda online</Typography>
            </Box>
          </Stack>
          <Box sx={styles.previewCart}>Carrito</Box>
        </Box>

        <Box sx={styles.previewHero}>
          <Typography sx={styles.previewHeroKicker}>NOVEDADES</Typography>
          <Typography sx={styles.previewHeroTitle}>
            Productos para sus clientes
          </Typography>
          <Box sx={styles.previewHeroButton}>Ver productos</Box>
        </Box>

        <Grid container spacing={1.2} sx={{ mt: 0.15 }}>
          {products.map((product, index) => (
            <Grid item xs={4} key={product.name}>
              <Box sx={styles.productCard}>
                <Box
                  sx={{
                    ...styles.productImage,
                    opacity: 1 - index * 0.08,
                  }}
                >
                  <StorefrontRounded sx={{ fontSize: 28 }} />
                </Box>
                <Typography sx={styles.productName}>{product.name}</Typography>
                <Typography sx={styles.productPrice}>{product.price}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper elevation={0} sx={styles.floatingStatus}>
        <Box sx={styles.statusIcon}>
          <CheckCircleRounded sx={{ fontSize: 20 }} />
        </Box>
        <Box>
          <Typography sx={styles.statusTitle}>Integrado con IntegraNube</Typography>
          <Typography sx={styles.statusText}>Información comercial conectada</Typography>
        </Box>
      </Paper>
    </Box>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    overflowX: 'hidden',
    background:
      'linear-gradient(180deg, #ffffff 0%, #f7f9fc 44%, #f4f7fb 100%)',
  },
  header: {
    position: 'relative',
    zIndex: 5,
    background: 'rgba(255,255,255,0.86)',
    backdropFilter: 'blur(16px)',
    borderBottom: '1px solid rgba(15,23,42,0.07)',
  },
  brandMark: {
    width: 40,
    height: 40,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#1769e0',
    color: '#ffffff',
    boxShadow: '0 8px 24px rgba(23,105,224,0.24)',
  },
  brandName: {
    fontSize: 16,
    lineHeight: 1.1,
    fontWeight: 800,
    letterSpacing: '-0.02em',
  },
  brandProduct: {
    mt: 0.25,
    fontSize: 11,
    color: '#64748b',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  headerButton: {
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: '10px',
    px: { xs: 1, sm: 1.5 },
  },
  hero: {
    position: 'relative',
    pt: { xs: 7, md: 10 },
    pb: { xs: 8, md: 12 },
    background:
      'radial-gradient(circle at 16% 18%, rgba(23,105,224,0.10), transparent 29%), radial-gradient(circle at 88% 24%, rgba(56,189,248,0.12), transparent 30%)',
  },
  eyebrow: {
    height: 32,
    px: 0.5,
    borderRadius: '999px',
    background: '#eaf2ff',
    color: '#1556b8',
    fontWeight: 800,
    '& .MuiChip-icon': {
      color: '#1769e0',
    },
  },
  heroTitle: {
    mt: 2.5,
    maxWidth: 680,
    fontSize: { xs: '2.55rem', sm: '3.3rem', md: '4rem' },
    lineHeight: 1.02,
    letterSpacing: '-0.052em',
    fontWeight: 850,
    color: '#0f172a',
  },
  heroAccent: {
    color: '#1769e0',
  },
  heroText: {
    mt: 2.5,
    maxWidth: 620,
    fontSize: { xs: 17, md: 19 },
    lineHeight: 1.65,
    color: '#5b6474',
  },
  primaryButton: {
    minHeight: 48,
    px: 2.4,
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 800,
    boxShadow: '0 12px 30px rgba(23,105,224,0.24)',
  },
  secondaryButton: {
    minHeight: 48,
    px: 2.4,
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 800,
    borderColor: '#cbd5e1',
    color: '#1e293b',
    background: 'rgba(255,255,255,0.72)',
  },
  checkIcon: {
    color: '#16a34a',
    fontSize: 18,
  },
  heroCheckText: {
    color: '#475569',
    fontSize: 13.5,
    fontWeight: 650,
  },
  previewWrap: {
    position: 'relative',
    maxWidth: 590,
    mx: 'auto',
    py: 2,
  },
  glowOne: {
    position: 'absolute',
    width: 220,
    height: 220,
    borderRadius: '50%',
    top: -20,
    right: -30,
    background: 'rgba(23,105,224,0.16)',
    filter: 'blur(48px)',
  },
  glowTwo: {
    position: 'absolute',
    width: 180,
    height: 180,
    borderRadius: '50%',
    bottom: -20,
    left: -30,
    background: 'rgba(14,165,233,0.15)',
    filter: 'blur(50px)',
  },
  browser: {
    position: 'relative',
    zIndex: 2,
    p: { xs: 1.2, sm: 1.7 },
    borderRadius: { xs: '18px', sm: '24px' },
    border: '1px solid rgba(15,23,42,0.10)',
    background: 'rgba(255,255,255,0.96)',
    boxShadow: '0 30px 80px rgba(15,23,42,0.16)',
  },
  browserTop: {
    pb: 1.3,
    px: 0.3,
  },
  browserDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#cbd5e1',
    flexShrink: 0,
  },
  browserUrl: {
    ml: '10px !important',
    flex: 1,
    height: 24,
    borderRadius: '7px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f1f5f9',
    color: '#64748b',
    fontSize: 10,
    fontWeight: 650,
  },
  previewStoreHeader: {
    px: { xs: 1.2, sm: 1.8 },
    py: 1.2,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: '12px 12px 0 0',
    border: '1px solid #eef2f7',
    borderBottom: 0,
  },
  previewLogo: {
    width: 31,
    height: 31,
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0f172a',
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 900,
  },
  previewBrand: {
    fontSize: 9.5,
    fontWeight: 900,
    letterSpacing: '0.06em',
  },
  previewBrandSub: {
    fontSize: 8.5,
    color: '#94a3b8',
  },
  previewCart: {
    px: 1.1,
    py: 0.6,
    borderRadius: '7px',
    background: '#f1f5f9',
    color: '#334155',
    fontSize: 8.5,
    fontWeight: 800,
  },
  previewHero: {
    px: { xs: 2, sm: 2.8 },
    py: { xs: 2.5, sm: 3.2 },
    minHeight: 145,
    background:
      'linear-gradient(125deg, #0f172a 0%, #1d4ed8 56%, #38bdf8 145%)',
    color: '#ffffff',
    borderRadius: '0 0 14px 14px',
  },
  previewHeroKicker: {
    fontSize: 8,
    letterSpacing: '0.13em',
    fontWeight: 900,
    opacity: 0.72,
  },
  previewHeroTitle: {
    mt: 0.8,
    maxWidth: 250,
    fontSize: { xs: 19, sm: 24 },
    lineHeight: 1.12,
    fontWeight: 850,
    letterSpacing: '-0.03em',
  },
  previewHeroButton: {
    mt: 1.6,
    width: 'fit-content',
    px: 1.2,
    py: 0.65,
    borderRadius: '7px',
    background: '#ffffff',
    color: '#0f172a',
    fontSize: 8.5,
    fontWeight: 900,
  },
  productCard: {
    p: { xs: 0.7, sm: 0.9 },
    borderRadius: '11px',
    border: '1px solid #eef2f7',
    background: '#ffffff',
  },
  productImage: {
    height: { xs: 58, sm: 76 },
    mb: 0.7,
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(145deg, #eef4ff, #f8fafc)',
    color: '#7c9bd0',
  },
  productName: {
    fontSize: { xs: 7.4, sm: 8.4 },
    color: '#475569',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  productPrice: {
    mt: 0.3,
    fontSize: { xs: 9, sm: 10 },
    color: '#0f172a',
    fontWeight: 900,
  },
  floatingStatus: {
    position: 'absolute',
    zIndex: 3,
    right: { xs: 6, sm: -16 },
    bottom: { xs: -18, sm: -20 },
    display: 'flex',
    alignItems: 'center',
    gap: 1.1,
    p: 1.4,
    pr: 1.8,
    borderRadius: '14px',
    border: '1px solid rgba(15,23,42,0.09)',
    boxShadow: '0 18px 45px rgba(15,23,42,0.14)',
  },
  statusIcon: {
    width: 35,
    height: 35,
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#15803d',
    background: '#dcfce7',
  },
  statusTitle: {
    fontSize: 11.5,
    fontWeight: 850,
    color: '#0f172a',
  },
  statusText: {
    mt: 0.2,
    fontSize: 9.5,
    color: '#64748b',
  },
  featuresSection: {
    py: { xs: 8, md: 11 },
    background: '#ffffff',
    borderTop: '1px solid #edf1f6',
    borderBottom: '1px solid #edf1f6',
  },
  sectionIntro: {
    maxWidth: 760,
    mx: 'auto',
    mb: 5.5,
    textAlign: 'center',
  },
  sectionKicker: {
    color: '#1769e0',
    fontSize: 11.5,
    fontWeight: 900,
    letterSpacing: '0.14em',
  },
  sectionTitle: {
    mt: 1.3,
    fontSize: { xs: '2rem', md: '2.65rem' },
    lineHeight: 1.1,
    letterSpacing: '-0.04em',
    fontWeight: 850,
    color: '#0f172a',
  },
  sectionText: {
    mt: 1.6,
    color: '#64748b',
    fontSize: { xs: 15.5, md: 17 },
    lineHeight: 1.7,
  },
  featureCard: {
    height: '100%',
    p: 3,
    borderRadius: '18px',
    border: '1px solid #e8edf4',
    transition: 'transform 180ms ease, box-shadow 180ms ease',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 18px 46px rgba(15,23,42,0.08)',
    },
  },
  featureIcon: {
    width: 46,
    height: 46,
    mb: 2.2,
    borderRadius: '13px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#edf4ff',
    color: '#1769e0',
  },
  featureTitle: {
    fontSize: 17,
    fontWeight: 850,
    letterSpacing: '-0.015em',
  },
  featureText: {
    mt: 0.9,
    color: '#64748b',
    fontSize: 14.2,
    lineHeight: 1.65,
  },
  domainSection: {
    py: { xs: 7, md: 10 },
  },
  domainCard: {
    p: { xs: 3, md: 5.5 },
    borderRadius: { xs: '22px', md: '28px' },
    border: '1px solid #e4eaf2',
    background:
      'linear-gradient(135deg, #ffffff 0%, #f3f7ff 52%, #eef9ff 100%)',
    boxShadow: '0 22px 60px rgba(15,23,42,0.07)',
  },
  domainTitle: {
    mt: 1.3,
    fontSize: { xs: '1.85rem', md: '2.4rem' },
    lineHeight: 1.12,
    letterSpacing: '-0.035em',
    fontWeight: 850,
  },
  domainText: {
    mt: 1.5,
    maxWidth: 650,
    color: '#64748b',
    lineHeight: 1.7,
    fontSize: 15.5,
  },
  domainBrowser: {
    p: 2,
    borderRadius: '17px',
    border: '1px solid #dfe7f1',
    background: 'rgba(255,255,255,0.9)',
    boxShadow: '0 16px 40px rgba(15,23,42,0.08)',
  },
  domainAddress: {
    px: 1.3,
    py: 1,
    borderRadius: '9px',
    background: '#f1f5f9',
    color: '#334155',
    fontSize: 12.5,
    fontWeight: 700,
  },
  domainMiniLabel: {
    color: '#1769e0',
    fontSize: 9.5,
    fontWeight: 900,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
  },
  domainMiniTitle: {
    mt: 0.7,
    color: '#0f172a',
    fontSize: 18,
    lineHeight: 1.25,
    fontWeight: 850,
  },
  ctaSection: {
    py: { xs: 8, md: 11 },
    color: '#ffffff',
    background:
      'radial-gradient(circle at 20% 0%, rgba(59,130,246,0.28), transparent 33%), linear-gradient(135deg, #0b1220 0%, #111d34 55%, #13274a 100%)',
  },
  ctaTitle: {
    fontSize: { xs: '2rem', md: '2.7rem' },
    lineHeight: 1.1,
    letterSpacing: '-0.04em',
    fontWeight: 850,
  },
  ctaText: {
    maxWidth: 700,
    mx: 'auto',
    mt: 1.7,
    color: '#cbd5e1',
    fontSize: { xs: 15.5, md: 17 },
    lineHeight: 1.7,
  },
  ctaButton: {
    mt: 3.2,
    minHeight: 50,
    px: 2.7,
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 850,
    background: '#ffffff',
    color: '#0f172a',
    '&:hover': {
      background: '#f8fafc',
    },
  },
  footer: {
    py: 2.7,
    background: '#080e19',
    color: '#94a3b8',
    borderTop: '1px solid rgba(255,255,255,0.06)',
  },
  footerText: {
    fontSize: 12.5,
  },
};

export default RetailLanding;
